import { createTheme} from '@mui/material/styles';
import { tokens, palette } from './tokens';

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: tokens.colors.primary,
      light: tokens.colors.primaryLight,
      dark: tokens.colors.primaryDark,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: palette.secondary[500],
      light: palette.secondary[400],
      dark: palette.secondary[800],
      contrastText: '#FFFFFF',
    },
    background: {
      default: tokens.colors.surfaceOnTop,
      paper: tokens.colors.surfaceDefault,
    },
    text: {
      primary: tokens.colors.textPrimary,
      secondary: tokens.colors.textSecondary,
      disabled: 'rgba(0, 0, 0, 0.38)',
    },
    divider: tokens.colors.border,
    info: {
      main: tokens.colors.info,
      light: '#5EB8FF',
      dark: '#01579B',
    },
    success: {
      main: tokens.colors.success,
      light: palette.feedback.success[100],
      dark: palette.feedback.success[700],
    },
    warning: {
      main: tokens.colors.warning,
      light: palette.feedback.warning[100],
      dark: palette.feedback.warning[700],
    },
    error: {
      main: tokens.colors.error,
      light: palette.feedback.error[100],
      dark: palette.feedback.error[700],
    },
  },
  typography: {
    fontFamily: tokens.typography.fontFamily,
    /* Figma Foundations type scale */
    h1: { fontSize: '2rem', lineHeight: 1.25, fontWeight: 400 },         // 32px/40px
    h2: { fontSize: '1.75rem', lineHeight: 1.29, fontWeight: 400 },      // 28px/36px
    h3: { fontSize: '1.5rem', lineHeight: 1.33, fontWeight: 400 },       // 24px/32px
    h4: { fontSize: '1.25rem', lineHeight: 1.4, fontWeight: 400 },       // 20px/28px
    h5: { fontSize: '1.125rem', lineHeight: 1.2, fontWeight: 400 },      // 18px/~22px
    h6: { fontSize: '1rem', lineHeight: 1.5, fontWeight: 500 },          // 16px/24px (Button-like)
    subtitle1: { fontSize: '1.0625rem', lineHeight: 1.53, fontWeight: 300 }, // Body L 17px/26px
    subtitle2: { fontSize: '0.9375rem', lineHeight: 1.47, fontWeight: 500 }, // Body M medium
    body1: { fontSize: '0.9375rem', lineHeight: 1.47, fontWeight: 300 },  // Body M 15px/22px
    body2: { fontSize: '0.875rem', lineHeight: 1.43, fontWeight: 300 },   // Body S 14px/20px
    button: { fontSize: '0.9375rem', lineHeight: 1.33, fontWeight: 500, textTransform: 'none' as const }, // 15px/20px
    caption: { fontSize: '0.6875rem', lineHeight: 1.45, fontWeight: 400 }, // Detail 11px/16px
    overline: { fontSize: '0.6875rem', lineHeight: 1.45, fontWeight: 500, textTransform: 'uppercase' as const },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: tokens.shape.borderRadius,
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
          border: `1px solid ${tokens.colors.border}`,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.shape.borderRadiusSmall,
          textTransform: 'none' as const,
          fontWeight: 500,
          padding: '8px 16px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: tokens.shape.borderRadiusChip,
          fontWeight: 500,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 8,
          backgroundColor: 'rgba(17, 126, 103, 0.1)',
        },
        bar: {
          borderRadius: 4,
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.colors.surfaceDefault,
          color: tokens.colors.textPrimary,
          boxShadow: 'none',
          borderBottom: `1px solid ${tokens.colors.border}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: tokens.colors.surfaceDefault,
          borderRight: `1px solid ${tokens.colors.border}`,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: tokens.shape.borderRadiusSmall,
          margin: '2px 8px',
          '&:hover': {
            backgroundColor: 'rgba(17, 126, 103, 0.08)',
          },
          '&.Mui-selected': {
            backgroundColor: 'rgba(17, 126, 103, 0.12)',
            color: tokens.colors.primary,
            '&:hover': {
              backgroundColor: 'rgba(17, 126, 103, 0.16)',
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
              borderColor: tokens.colors.primaryLight,
            },
            '&.Mui-focused fieldset': {
              borderColor: tokens.colors.primary,
              borderWidth: 2,
            },
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            backgroundColor: 'rgba(17, 126, 103, 0.08)',
          },
        },
      },
    },
  },
  breakpoints: {
    values: tokens.grid.breakpoints,
  },
  spacing: 8,
});
